/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

const ICON_MAP = {
  Attention: "center_focus_strong",
  Language: "translate",
  Memory: "psychology",
  Orientation: "explore",
  "Reaction Time": "speed",
  "Visual Memory": "visibility",
  Visuospatial: "grid_view",
  "Executive Functions": "account_tree",
};

const COLOR_MAP = {
  Attention: "primary",
  Language: "secondary",
  Memory: "info",
  Orientation: "success",
  "Reaction Time": "warning",
  "Visual Memory": "error",
  Visuospatial: "info",
  "Executive Functions": "warning",
};

function getTrend(score, cat, wellness) {
  const raw = parseFloat(score);
  if (!cat || isNaN(raw) || raw === 0) return { label: "No data yet", color: "#9e9e9e", dot: "○", text: "" };
  // API returns 0-10; normalise to 0-1 for threshold comparison
  const s = raw > 1.0 ? raw / 10.0 : raw;
  if (s >= 0.75) return { label: "Good", color: "#4caf50", dot: "●", text: cat.trendHigh || "" };
  if (s >= 0.4)  return { label: "Moderate", color: "#ff9800", dot: "●", text: cat.trendMid || "" };
  return { label: wellness ? "Room to grow" : "Needs attention", color: "#f44336", dot: "●", text: cat.trendLow || "" };
}

function ComplexStatisticsCard({ color, title, count, percentage, icon, ...props }) {
  const bgColor = COLOR_MAP[title] || "info";
  const iconName = ICON_MAP[title] || "star";
  const trend = getTrend(count, props.catData, props.wellness);

  return (
    <Card style={{ background: props.cat?.name === title ? "lemonchiffon" : undefined, height: "100%" }}>
      <MDBox display="flex" justifyContent="space-between" pt={1} px={2}>
        <MDBox
          variant="gradient"
          bgColor={bgColor}
          color="white"
          coloredShadow={bgColor}
          borderRadius="xl"
          display="flex"
          justifyContent="center"
          alignItems="center"
          width="4rem"
          height="4rem"
          mt={-3}
        >
          <Icon fontSize="medium" color="inherit">{iconName}</Icon>
        </MDBox>
        <MDBox textAlign="right" lineHeight={1.25}>
          <MDTypography variant="button" fontWeight="light" color="text">
            {title}
          </MDTypography>
          <MDTypography variant="h4">{count}</MDTypography>
        </MDBox>
      </MDBox>

      <Divider />

      <MDBox pb={2} px={2}>
        {/* Insight description */}
        {props.catData?.insight && (
          <MDTypography variant="caption" color="text" display="block" mb={1} lineHeight={1.5}>
            {props.catData.insight}
          </MDTypography>
        )}

        {/* Trend badge + message */}
        <MDBox display="flex" alignItems="flex-start" gap={0.5} mt={0.5}>
          <MDTypography
            variant="caption"
            fontWeight="bold"
            style={{ color: trend.color, whiteSpace: "nowrap", marginTop: "1px" }}
          >
            {trend.dot}&nbsp;{trend.label}
          </MDTypography>
          {trend.text && (
            <MDTypography variant="caption" color="text" lineHeight={1.4}>
              — {trend.text}
            </MDTypography>
          )}
        </MDBox>
      </MDBox>
    </Card>
  );
}

// Setting default values for the props of ComplexStatisticsCard
ComplexStatisticsCard.defaultProps = {
  color: "info",
  percentage: {
    color: "success",
    text: "",
    label: "",
  },
};

// Typechecking props for the ComplexStatisticsCard
ComplexStatisticsCard.propTypes = {
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "light",
    "dark",
  ]),
  title: PropTypes.string.isRequired,
  count: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  percentage: PropTypes.shape({
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "dark",
      "white",
    ]),
    amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.string,
  }),
  icon: PropTypes.node.isRequired,
};

export default ComplexStatisticsCard;
